import { faArrowRightArrowLeft } from '@fortawesome/pro-light-svg-icons';
import {
  Action,
  Card,
  Icon,
  Menu,
  SimpleSelect,
  SupportedIcon,
  TextAreaInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import { CrewIncentiveTypeEnum, Pegasus } from 'corso-types';
import { Controller, useFieldArray } from 'react-hook-form';
import MonetaryRateInput from '~/components/field/MonetaryRateInput';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useCurrency } from '~/hooks/useCurrency';

const ShopNowIcon = {
  MagnifyingGlass: 'search',
  DollarSign: 'dollar',
  ArrowRightArrowLeft: faArrowRightArrowLeft,
  Truck: 'shipment',
} as const satisfies Record<Pegasus.ShopNowIcon, SupportedIcon>;

const ShopNowIconLabel = {
  MagnifyingGlass: 'Magnifying Glass',
  DollarSign: 'Dollar Sign',
  ArrowRightArrowLeft: 'Exchange',
  Truck: 'Truck',
} as const satisfies Record<Pegasus.ShopNowIcon, string>;

const iconSelectOptions = Object.values(Pegasus.ShopNowIcon).map((icon) => ({
  value: icon,
  label: (
    <span className="flex items-center gap-1">
      <Icon icon={ShopNowIcon[icon]} /> {ShopNowIconLabel[icon]}
    </span>
  ),
}));

const benefitKindText = {
  [Pegasus.ShopNowBenefitKind.informational]: 'Informational',
  [Pegasus.ShopNowBenefitKind.incentive]: 'Offer An Incentive',
} as const;

export default function EventShopNow() {
  const { currencyCode, currencySymbol } = useCurrency();
  const { register, control, watch } = useAutomationFormContext();

  const {
    fields: shopNowBenefitFields, // TODO default needed (RHF field issues)?
    append: addBenefit,
    remove: removeBenefit,
  } = useFieldArray({
    name: 'event.params.shopNowBenefits',
    control,
  });

  const handleAddBenefit = (kind: Pegasus.ShopNowBenefitKind) => {
    if (kind === Pegasus.ShopNowBenefitKind.incentive) {
      addBenefit({
        kind: 'incentive' as const,
        incentive: {
          amount: 10,
          type: CrewIncentiveTypeEnum.fixed,
        },
        icon: 'DollarSign' as const,
        label: 'Make more for your return',
        description:
          'Because we think we you can find something new to love, we have added a little extra to your return credit.',
      });
      return;
    }

    addBenefit({
      kind: 'informational' as const,
      icon: 'MagnifyingGlass' as const,
      label: 'Explore our latest arrivals',
      description:
        'Browse the latest arrivals chosen to complement what you already love.',
    });
  };

  const currentBenefitKinds = watch('event.params.shopNowBenefits').map(
    (benefit) => benefit.kind,
  );

  const hasIncentiveBenefit = currentBenefitKinds.includes(
    Pegasus.ShopNowBenefitKind.incentive,
  );

  const benefitCards = shopNowBenefitFields.map((benefit, index) => (
    <Card key={benefit.id}>
      <div className="flex flex-col gap-2">
        {/* // TODO incentive amount and value */}
        <TextInput
          id={`shop-now-benefit-label-${index}`}
          {...register(`event.params.shopNowBenefits.${index}.label`)}
          label="Label"
          required
        />
        <Controller
          control={control}
          name={`event.params.shopNowBenefits.${index}.icon`}
          render={({ field: { onChange, value }, fieldState }) => (
            <SimpleSelect
              options={iconSelectOptions}
              value={value}
              onChange={onChange}
              label="Icon"
              error={fieldState.error?.message}
            />
          )}
        />
        <TextAreaInput
          id={`shop-now-benefit-description-${index}`}
          {...register(`event.params.shopNowBenefits.${index}.description`)}
          label="Description"
          required
        />
        {benefit.kind === Pegasus.ShopNowBenefitKind.incentive && (
          <Controller
            control={control}
            name={`event.params.shopNowBenefits.${index}.incentive`}
            render={({ field: { onChange, value }, fieldState }) => (
              <MonetaryRateInput
                id={`shop-now-benefit-incentive-${index}`}
                label="Incentive Amount"
                required
                currencyCode={currencyCode}
                currencySymbol={currencySymbol}
                value={value}
                onChange={onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        )}

        <div className="mt-2 self-end">
          <Action onClick={() => removeBenefit(index)} variant="destructive">
            Remove
          </Action>
        </div>
      </div>
    </Card>
  ));

  return (
    <>
      <UrlInput
        id="shop-now-start-shopping-url"
        {...register('event.params.startShoppingUrl')}
        label="Start Shop Now URL"
        details="The URL the customer will be directed to when starting a new Shop Now Session."
        required
      />
      <Card.Line>
        <Card.Heading>Benefits</Card.Heading>
        <Menu
          buttonAs={
            <Action
              variant="ghost"
              icon="add"
              accessibilityLabel="Add Benefit"
              disabled={shopNowBenefitFields.length >= 4}
            />
          }
        >
          {Object.values(Pegasus.ShopNowBenefitKind).map((kind) => (
            <Menu.ItemButton
              key={kind}
              onClick={() => handleAddBenefit(kind)}
              // only one incentive benefit allowed
              disabled={
                hasIncentiveBenefit &&
                kind === Pegasus.ShopNowBenefitKind.incentive
              }
              hidden={
                hasIncentiveBenefit &&
                kind === Pegasus.ShopNowBenefitKind.incentive
              }
            >
              {benefitKindText[kind]}
            </Menu.ItemButton>
          ))}
        </Menu>
      </Card.Line>

      {benefitCards}
    </>
  );
}
